import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { isDomainError } from '../domain/errors';
import type {
  AcceptResult,
  Inviter,
  InvitationSource,
  InvitePreview,
  PreviewOffer,
} from '../domain/invitations';
import type { PersonRef } from '../domain/people';
import { useRepositories } from '../hooks/useRepositories';
import { fullName } from '../utils/people';
import { ActionError, EmptyState, LoadingState, StateForError } from './PageStates';
import PostAcceptStep from './PostAcceptStep';
import UserAvatar from './UserAvatar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Check, Hourglass, Lock } from 'lucide-react';

type SignedOutInviter = Extract<Inviter, { kind: 'initialOnly' }>;

type Load =
  | { state: 'loading' }
  | { state: 'expired' }
  | { state: 'failed'; error: unknown }
  | { state: 'ready'; preview: InvitePreview }
  | { state: 'connected'; result: AcceptResult };

/**
 * What an invitation offers me, and answering it — the one invitation screen,
 * read from a link I opened or an invite waiting for me (014 R14).
 *
 * Every unusable invitation reads "This invitation has expired.", whatever the
 * reason; one that offers nothing new says so instead of offering Accept.
 * Accepting is shown as done only once it succeeds; an accept that starts a
 * connection is followed by the chance to mute the inviter's collections.
 */
export default function InvitationView({ source }: { source: InvitationSource }) {
  const navigate = useNavigate();
  const { invitations, session } = useRepositories();
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<'accepting' | 'declining' | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);

  // A new source object for the same invitation isn't a new load.
  const token = source.kind === 'link' ? source.token : null;
  const invitationId = source.kind === 'received' ? source.invitationId : null;

  const fetchPreview = useCallback(async () => {
    setLoad({ state: 'loading' });
    try {
      const preview =
        token !== null
          ? await invitations.preview(token)
          : await invitations.receivedInvitation(invitationId!);
      setChecked(new Set(preview.offers.filter((o) => o.checked).map((o) => o.offerId)));
      setLoad({ state: 'ready', preview });
    } catch (error) {
      setLoad(
        isDomainError(error, 'invitationExpired')
          ? { state: 'expired' }
          : { state: 'failed', error },
      );
    }
  }, [invitations, token, invitationId]);

  useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  if (load.state === 'loading') return <LoadingState label="Loading invitation" />;
  if (load.state === 'expired') return <ExpiredState />;
  if (load.state === 'failed') return <StateForError error={load.error} onRetry={fetchPreview} />;
  if (load.state === 'connected') {
    return <PostAcceptStep inviter={load.result.inviter} joined={load.result.joined} />;
  }

  const { preview } = load;
  const available = preview.offers.filter((o) => o.status === 'available');
  const choosesSomethingNew = preview.connects || available.some((o) => checked.has(o.offerId));

  const toggle = (offerId: string, on: boolean) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (on) next.add(offerId);
      else next.delete(offerId);
      return next;
    });

  const accept = async () => {
    setBusy('accepting');
    setActionError(null);
    const joined = available.filter((o) => checked.has(o.offerId)).map((o) => o.offerId);
    try {
      const result =
        source.kind === 'link'
          ? await invitations.accept(source.token, joined)
          : await invitations.acceptReceived(source.invitationId, joined);
      if (preview.connects) {
        setLoad({ state: 'connected', result });
      } else {
        toast.success(`You've joined ${fullName(result.inviter)}'s collections.`);
        navigate('/my-collections');
      }
    } catch (error) {
      if (isDomainError(error, 'invitationExpired')) setLoad({ state: 'expired' });
      else if (isDomainError(error, 'nothingNew')) fetchPreview();
      else setActionError(error);
    } finally {
      setBusy(null);
    }
  };

  const decline = async () => {
    setBusy('declining');
    setActionError(null);
    try {
      if (source.kind === 'link') await invitations.decline(source.token);
      else await invitations.declineReceived(source.invitationId);
      toast.success('Invitation declined.');
      navigate('/invitations');
    } catch (error) {
      if (isDomainError(error, 'invitationExpired')) setLoad({ state: 'expired' });
      else setActionError(error);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <section className="rounded-3xl bg-card p-8">
        {preview.inviter.kind === 'initialOnly' ? (
          <SignedOutHeader inviter={preview.inviter} />
        ) : (
          <SignedInHeader inviter={preview.inviter.person} connects={preview.connects} />
        )}

        {preview.offers.length > 0 && (
          <div className="mt-8">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Private collections on offer
            </h2>
            <ul className="space-y-2">
              {preview.offers.map((offer) => (
                <OfferRow
                  key={offer.offerId}
                  offer={offer}
                  choosable={preview.signedIn}
                  checked={checked.has(offer.offerId)}
                  onToggle={toggle}
                />
              ))}
            </ul>
          </div>
        )}

        <div className="mt-8 space-y-3">
          {!preview.signedIn ? (
            source.kind === 'link' && (
              <>
                <Button className="w-full rounded-full py-6 text-base font-bold" asChild>
                  <a href={session.signInUrl(`/invite/${source.token}`)}>Sign in to accept</a>
                </Button>
                <p className="text-center text-sm text-muted-foreground">
                  New here?{' '}
                  <Link
                    to={`/invite/${source.token}/sign-up`}
                    className="font-semibold text-primary hover:underline"
                  >
                    Sign up
                  </Link>
                </p>
              </>
            )
          ) : preview.nothingNew ? (
            <p className="rounded-2xl bg-muted p-4 text-center font-semibold text-foreground">
              Nothing new here
            </p>
          ) : (
            <Button
              className="w-full rounded-full py-6 text-base font-bold"
              disabled={!choosesSomethingNew || busy !== null}
              onClick={accept}
            >
              {busy === 'accepting' ? 'Accepting…' : 'Accept'}
            </Button>
          )}
          {preview.signedIn && preview.canDecline && (
            <Button
              variant="secondary"
              className="w-full rounded-full py-6 text-base font-semibold"
              disabled={busy !== null}
              onClick={decline}
            >
              {busy === 'declining' ? 'Declining…' : 'Decline'}
            </Button>
          )}
        </div>
      </section>

      {actionError !== null && <ActionError error={actionError} />}
    </div>
  );
}

function ExpiredState() {
  return (
    <div className="mx-auto max-w-xl">
      <EmptyState
        icon={<Hourglass className="size-8" />}
        title="This invitation has expired."
        description="Ask whoever sent it for a new link."
      />
    </div>
  );
}

function SignedInHeader({ inviter, connects }: { inviter: PersonRef; connects: boolean }) {
  return (
    <div className="flex items-center gap-4">
      <UserAvatar person={inviter} className="size-16" textClassName="text-xl" labelled />
      <div>
        <p className="text-sm font-medium text-muted-foreground">You're invited by</p>
        <p className="text-2xl font-bold tracking-tight text-foreground">{fullName(inviter)}</p>
        {connects && (
          <p className="mt-1 text-sm text-muted-foreground">
            Accepting connects you — you'll each see the other's open collections.
          </p>
        )}
      </div>
    </div>
  );
}

function SignedOutHeader({ inviter }: { inviter: SignedOutInviter }) {
  return (
    <div>
      <p className="text-sm font-medium text-muted-foreground">You're invited by</p>
      <p className="text-2xl font-bold tracking-tight text-foreground">
        {`${inviter.firstName} ${inviter.lastInitial}.`}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        Sign in or sign up to see the invitation and accept.
      </p>
    </div>
  );
}

function OfferRow({
  offer,
  choosable,
  checked,
  onToggle,
}: {
  offer: PreviewOffer;
  choosable: boolean;
  checked: boolean;
  onToggle: (offerId: string, on: boolean) => void;
}) {
  const id = `offer-${offer.offerId}`;
  if (!choosable) {
    return (
      <li className="flex items-center gap-3 rounded-2xl bg-muted px-4 py-3">
        <Lock className="size-4 text-wood" />
        <span className="font-medium text-foreground">{offer.collectionName}</span>
      </li>
    );
  }
  if (offer.status === 'alreadyFollowing') {
    return (
      <li className="flex items-center justify-between gap-3 rounded-2xl bg-muted px-4 py-3">
        <span className="font-medium text-foreground">{offer.collectionName}</span>
        <span className="inline-flex items-center gap-1 rounded-full bg-wood/15 px-3 py-1 text-xs font-semibold text-wood">
          <Check className="size-3" />
          Already following
        </span>
      </li>
    );
  }
  return (
    <li className="flex items-center gap-3 rounded-2xl bg-muted px-4 py-3">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(value) => onToggle(offer.offerId, value === true)}
      />
      <Label htmlFor={id} className="flex-1 cursor-pointer font-medium text-foreground">
        {offer.collectionName}
      </Label>
    </li>
  );
}
